import { DynamicForm } from "@shared/components/DynamicForm";
import { SearchInput } from "@shared/components/SearchInput";
import { StepHelpers } from "@shared/components/Stepper";
import { toast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FileText, Mail, MapPin, Phone, User, Users } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useConsumerMetaData, useConsumerSearch } from "../../../hooks";
import { ServiceRequestFormData } from "../../../types";
import { formatTerritoryAddress } from "@shared/utils/address";

interface CustomerSearchResult {
  id: string;
  label: string;
  description?: string;
  accountNumber: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  customerType?: string;
}

interface CustomerInformationStepProps {
  remoteUtilityId: number;
  storageKey: string;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  // New props for URL parameters
  urlConsumerId?: number | null;
  urlConsumerNo?: string | null;
}

export const CustomerInformationStep: React.FC<
  CustomerInformationStepProps
> = ({
  remoteUtilityId,
  storageKey,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
  urlConsumerId,
  urlConsumerNo,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedConsumerId, setSelectedConsumerId] = useState<number | null>(
    null
  );
  const [isMetadataProcessed, setIsMetadataProcessed] = useState(false);
  const [isAccountLoaded, setIsAccountLoaded] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const dataLoadedRef = useRef(false);
  const urlParamsProcessedRef = useRef(false);
  const searchInputRef = useRef<any>(null);

  // Use the consumer search hook
  const { data: searchResults, isLoading: isSearching } = useConsumerSearch({
    remoteUtilityId,
    requestType: "Service",
    searchData: searchTerm,
  });

  // Use the consumer metadata hook to get detailed customer information
  const { data: consumerMetadata } = useConsumerMetaData({
    consumerId: selectedConsumerId || 0,
    remoteUtilityId,
  });

  // Helper function to save step data using stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
        console.log("Step data saved via stepHelpers:", stepData);
      } catch (error) {
        console.error("Failed to save step data via stepHelpers:", error);
        toast({
          title: "Failed to save progress",
          description: "Your progress may not be saved. Please try again.",
          variant: "destructive",
        });
      }
    },
    [stepHelpers, currentStepIndex]
  );

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Create form instance using FormService
  const customerFields: FormField[] = [
    {
      name: "customerName",
      label: "Customer Name",
      type: "text",
      required: true,
      placeholder: "Enter customer name",
      disabled: true,
      helperText: "Full name of the customer as it appears on their account",
      showHelperTooltip: true,
      tooltipText: "Full name of the customer as it appears on their account.",
      fullWidth: true,
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-12 px-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "serviceAddress",
      label: "Service Address",
      type: "text",
      required: true,
      placeholder: "Enter service address",
      disabled: true,
      helperText: "The address where the service will be provided",
      showHelperTooltip: true,
      tooltipText:
        "The address where the service will be provided or where the issue is located.",
      fullWidth: true,
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-12 px-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "contactPhone",
      label: "Contact Phone (Optional)",
      type: "phone",
      required: true,
      placeholder: "(000) 000-0000",
      disabled: true,
      helperText: "Primary phone number for service updates",
      showHelperTooltip: true,
      tooltipText:
        "Primary phone number where we can reach the customer for updates about their service request.",
      fullWidth: true,
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-12 px-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "contactEmail",
      label: "Contact Email (Optional)",
      type: "email",
      placeholder: "customer@example.com",
      disabled: true,
      helperText: "Email address for confirmations and updates",
      showHelperTooltip: true,
      tooltipText:
        "Email address for sending service confirmations and updates. This is optional but recommended.",
      fullWidth: true,
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-12 px-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
  ];

  // Create Form Instance
  const customerForm = FormService.createForm({
    fields: customerFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      accountNumber: "",
      customerName: "",
      serviceAddress: "",
      contactPhone: "",
      contactEmail: "",
    },
  });

  // Process URL parameters for auto-population
  useEffect(() => {
    if (
      urlConsumerId && 
      urlConsumerNo && 
      !urlParamsProcessedRef.current && 
      !dataLoadedRef.current
    ) {
      console.log("Processing URL parameters:", { urlConsumerId, urlConsumerNo });
      
      // Set the search term to trigger search
      setSearchTerm(urlConsumerNo);
      
      // Set the selected consumer ID
      setSelectedConsumerId(urlConsumerId);
      
      // Mark URL params as processed
      urlParamsProcessedRef.current = true;
      
      console.log("URL parameters processed, search term set to:", urlConsumerNo);
    }
  }, [urlConsumerId, urlConsumerNo]);

  // Auto-select customer when search results are available and URL params are present
  useEffect(() => {
    if (
      urlConsumerId && 
      urlConsumerNo && 
      searchResults?.result && 
      urlParamsProcessedRef.current &&
      !dataLoadedRef.current
    ) {
      // Find the matching consumer in search results
      const matchingConsumer = searchResults.result.find(
        (consumer) => consumer.id === urlConsumerId && consumer.consumerNo === urlConsumerNo
      );

      if (matchingConsumer) {
        console.log("Auto-selecting consumer from URL params:", matchingConsumer);
        
        // Create customer search result object
        const customerResult: CustomerSearchResult = {
          id: matchingConsumer.id.toString(),
          label: `${matchingConsumer.firstName} ${matchingConsumer.lastName}`,
          description: `#${matchingConsumer.consumerNo}`,
          accountNumber: matchingConsumer.consumerNo,
          name: `${matchingConsumer.firstName} ${matchingConsumer.lastName}`,
          address: "",
          phone: "",
          email: "",
          customerType: matchingConsumer.statusDisplay,
        };

        // Auto-select the customer
        handleSelectCustomer(customerResult);
        
        // Ensure account is marked as loaded for URL-based selection
        setIsAccountLoaded(true);
        
        // Mark as processed to prevent re-processing
        dataLoadedRef.current = true;
      }
    }
  }, [searchResults, urlConsumerId, urlConsumerNo]);

  // Load saved data from stepHelpers on component mount (only if no URL params)
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current && !urlConsumerId && !urlConsumerNo) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          console.log("Loaded step data from stepHelpers:", savedStepData);

          // Populate form with saved data
          FormService.populateForm(customerForm, savedStepData, customerFields);

          // Restore selected customer if available
          if (savedStepData.selectedConsumerId) {
            setSelectedConsumerId(savedStepData.selectedConsumerId);
            setIsAccountLoaded(true);
          }
        }
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data from stepHelpers:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, customerForm, getStepData, urlConsumerId, urlConsumerNo]);

  // Update form with metadata when available
useEffect(() => {
  if (
    consumerMetadata?.result &&
    selectedConsumerId &&
    !isMetadataProcessed
  ) {
    const metadata = consumerMetadata.result;

    // Build address string using configured format
    const fullAddress = formatTerritoryAddress(
      { service: metadata.serviceAddress },
      "service"
    ) || "";

    // Update form with complete customer data
    const formData = {
      customerName: `${metadata.firstName} ${metadata.lastName}`,
      serviceAddress: fullAddress,
      contactPhone: metadata.contactNumber,
      contactEmail: metadata.email,
      accountNumber: metadata.consumerNo,
    };

    // Update form values
    Object.entries(formData).forEach(([key, value]) => {
      customerForm.setValue(key as any, value);
    });

    // Save complete data using stepHelpers
    saveStepData({
      ...formData,
      selectedConsumerId: selectedConsumerId,
      consumerMetadata: metadata,
    });

    // Mark metadata as processed to prevent re-running
    setIsMetadataProcessed(true);
    
    // Set account as loaded to enable the continue button
    setIsAccountLoaded(true);
    
    // Update search term to show customer name after loading
    if (urlConsumerId && urlConsumerNo) {
      setSearchTerm(`${metadata.firstName} ${metadata.lastName}`);
    }
  }
}, [
  consumerMetadata,
  customerForm,
  saveStepData,
  isMetadataProcessed,
  selectedConsumerId,
  urlConsumerId,
  urlConsumerNo,
]);

  // Reset metadata processed flag when a new customer is selected
  useEffect(() => {
    if (selectedConsumerId) {
      setIsMetadataProcessed(false);
    }
  }, [selectedConsumerId]);

  // Handle customer search
  const handleCustomerSearch = useCallback(
    async (term: string): Promise<CustomerSearchResult[]> => {
      if (term.length < 3) {
        return [];
      }

      setSearchTerm(term);

      // Map search results to the expected format
      const results =
        searchResults?.result?.map((consumer) => ({
          id: consumer.id.toString(),
          label: `${consumer.firstName} ${consumer.lastName}`,
          description: `#${consumer.consumerNo}`,
          accountNumber: consumer.consumerNo,
          name: `${consumer.firstName} ${consumer.lastName}`,
          address: "", // Will be populated when customer is selected
          phone: "", // Will be populated when customer is selected
          email: "", // Will be populated when customer is selected
          customerType: consumer.statusDisplay,
        })) || [];

      return results;
    },
    [searchResults]
  );

  // Handle customer selection
  const handleSelectCustomer = useCallback(
    (customer: CustomerSearchResult) => {
      const consumerId = parseInt(customer.id);
      setSelectedConsumerId(consumerId);

      // Update form with basic customer data
      customerForm.setValue("customerName", customer.name);
      customerForm.setValue("accountNumber", customer.accountNumber);

      // Save initial customer selection data using stepHelpers
      saveStepData({
        accountNumber: customer.accountNumber,
        customerName: customer.name,
        selectedConsumerId: consumerId,
        selectedCustomer: customer,
        isCustomerSelected: true,
      });

      setIsAccountLoaded(true);
    },
    [customerForm, saveStepData]
  );

  // Render customer search result
  const renderCustomerResult = useCallback(
    (result: CustomerSearchResult) => (
      <div className="p-3 hover:bg-blue-50 cursor-pointer rounded border-l-4 border-transparent hover:border-blue-400 transition-all">
        <div className="font-medium text-gray-900">{result.name}</div>
        <div className="text-sm text-gray-600">
          Account #{result.accountNumber}
        </div>
        {result.customerType && (
          <div className="text-xs text-gray-500">{result.customerType}</div>
        )}
      </div>
    ),
    []
  );

  const handleFormSubmit = (data: Partial<ServiceRequestFormData>) => {
    // Clear any previous validation errors
    if (clearValidationError) {
      clearValidationError();
    }

    // Validate required fields before proceeding
    const requiredFields = ["customerName", "serviceAddress", "contactPhone"];
    const missingFields = requiredFields.filter(
      (field) => !data[field as keyof ServiceRequestFormData]
    );

    if (missingFields.length > 0) {
      const errorMessage = `Please fill in all required fields: ${missingFields.join(
        ", "
      )}`;
      toast({
        title: errorMessage,
        variant: "destructive",
      });

      // Set validation error in stepper
      if (setValidationError) {
        setValidationError(errorMessage);
      }
      return;
    }

    // Save final form data using stepHelpers
    saveStepData({
      ...data,
      selectedConsumerId,
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    });

    // Proceed to next step
    if (onNext) {
      onNext();
    }
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    // Get current step data and update the specific field
    const currentData = getStepData() || {};
    const updatedData = {
      ...currentData,
      [fieldName]: value,
      lastUpdated: new Date().toISOString(),
    };

    // Save updated data using stepHelpers
    saveStepData(updatedData);
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-600">
          Search and select a customer to begin the service request.
        </p>
      </div>

      {/* Customer Search Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Users className="h-5 w-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">
            Customer Search
          </h3>
        </div>

        <div className="relative">
          <SearchInput
            ref={searchInputRef}
            placeholder="Search by account number, name, phone or address..."
            onSearch={handleCustomerSearch}
            onSelect={handleSelectCustomer}
            renderResult={renderCustomerResult}
            minSearchLength={3}
            className="w-full"
            debounceMs={200}
            initialValue={searchTerm}
            disabled={(urlConsumerId || urlConsumerNo) ? true : false}
          />
          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
            <FileText className="h-3 w-3" />
            Type at least 3 characters to search existing customers
          </p>
        </div>
      </div>

      {/* Selected Customer Information Display */}
      {selectedConsumerId && (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 mb-3">
            <User className="h-4 w-4 text-green-600" />
            <h4 className="font-medium text-green-800">Selected Customer</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-700">Account No:</span>
              <span className="text-gray-900">
                {customerForm.getValues().accountNumber || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-700">Name:</span>
              <span className="text-gray-900">
                {customerForm.getValues().customerName || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-3 w-3 text-gray-500" />
              <span className="font-medium text-gray-700">Phone:</span>
              <span className="text-gray-900">
                {customerForm.getValues().contactPhone || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-3 w-3 text-gray-500" />
              <span className="font-medium text-gray-700">Email:</span>
              <span className="text-gray-900">
                {customerForm.getValues().contactEmail || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <MapPin className="h-3 w-3 text-gray-500" />
              <span className="font-medium text-gray-700">Address:</span>
              <span className="text-gray-900">
                {customerForm.getValues().serviceAddress || "N/A"}
              </span>
            </div>
          </div>
        </div>
      )}

      <DynamicForm
        fields={customerFields}
        form={customerForm}
        onSubmit={handleFormSubmit}
        onFieldChange={handleFieldChange}
        config={{
          showSubmitButton: false,
          showResetButton: false,
        }}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
      />

      <div className="flex justify-end pt-4">
        <Button
          onClick={() => handleFormSubmit(customerForm.getValues())}
          disabled={!isAccountLoaded || isValidating}
          className="min-w-[200px]"
        >
          {isValidating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Validating...
            </>
          ) : (
            "Continue to Service Selection"
          )}
        </Button>
      </div>

      {/* Validation Error Display */}
      {validationErrors && Object.keys(validationErrors).length > 0 && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <h4 className="font-medium text-red-800">Validation Errors</h4>
          </div>
          <ul className="text-sm text-red-700 space-y-1">
            {Object.entries(validationErrors).map(([field, error]) => (
              <li key={field}>• {error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};